import type { Event, Offer, Thing, WithContext } from 'schema-dts';
import { Day } from '@/entities/day';
import type { Question } from '@/entities/question';
import type { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import { formatLocalIso8601CalendarDate } from '@/shared/lib/utils/dates';
import { composeSchemaGraph } from '@/shared/lib/utils/page-json-ld';
import {
    type SchemaContacts,
    type SchemaOfferInput,
    buildSchemaOffers,
    buildSchemaPlace,
    oneLine,
    schemaEventImage,
    schemaIdRef,
    stripHtml,
} from '@/shared/lib/utils/schema-common';
import {
    SCHEMA_EVENT_ATTENDANCE,
    SCHEMA_EVENT_STATUS,
    getSchemaIds,
    schemaPageUrl,
} from '@/shared/lib/utils/schema-ids';

export function computeEventDatesFromDays(days: Day[]): { startDateIso?: string; endDateIso?: string } {
    const timestamps = days
        .map((d) => new Date(d.publishedAt))
        .filter((d) => !Number.isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
    if (!timestamps.length) return {};
    const start = formatLocalIso8601CalendarDate(timestamps[0]);
    const end = formatLocalIso8601CalendarDate(timestamps[timestamps.length - 1]);
    return { startDateIso: start ?? undefined, endDateIso: end ?? undefined };
}

export type GenerateEventPageJsonLdInput = {
    name: string;
    descriptionHtml?: string;
    descriptionFallback?: string;
    metaDescription?: string;
    pagePath: string;
    imageSrc?: string | null;
    startDateIso?: string | null;
    endDateIso?: string | null;
    offers?: SchemaOfferInput[];
    breadcrumbs?: AppBreadcrumbs | null;
    faqQuestions?: Question[] | null;
    contacts?: SchemaContacts | null;
};

export function generateEventPageJsonLd({
    name,
    descriptionHtml,
    descriptionFallback,
    metaDescription,
    pagePath,
    imageSrc,
    startDateIso,
    endDateIso,
    offers,
    breadcrumbs,
    faqQuestions,
    contacts,
}: GenerateEventPageJsonLdInput): WithContext<Thing>[] {
    const ids = getSchemaIds();
    const pageUrl = schemaPageUrl(pagePath);
    const description =
        oneLine(stripHtml(metaDescription || '')) ||
        oneLine(stripHtml(descriptionHtml || '')) ||
        oneLine(stripHtml(descriptionFallback || ''));
    const eventImage = schemaEventImage(imageSrc);
    const schemaOffers: Offer[] = buildSchemaOffers(offers ?? []);

    const event: WithContext<Event> = {
        '@context': 'https://schema.org',
        '@type': 'Event',
        '@id': `${pageUrl}#event`,
        name: oneLine(name),
        url: pageUrl,
        ...(description ? { description } : {}),
        ...(startDateIso ? { startDate: startDateIso } : {}),
        ...(endDateIso ? { endDate: endDateIso } : {}),
        inLanguage: 'ru',
        eventStatus: SCHEMA_EVENT_STATUS,
        eventAttendanceMode: SCHEMA_EVENT_ATTENDANCE,
        ...(eventImage ? { image: eventImage } : {}),
        location: buildSchemaPlace(contacts),
        organizer: schemaIdRef(ids.organization) as Event['organizer'],
        superEvent: schemaIdRef(ids.forum) as Event['superEvent'],
        ...(schemaOffers.length > 0 ? { offers: schemaOffers } : {}),
    };

    return composeSchemaGraph({
        nodes: [event as WithContext<Thing>],
        breadcrumbs,
        faqQuestions,
        faqPageUrl: pageUrl,
    });
}
